// src/components/home/AnnouncementsPill.js
//
// The home page's "Announcements" pill (UI revamp). Collapsed by default. When
// there's a post the person hasn't been shown yet (newer than their saved
// seenAt), it opens by itself, shows "N new" with a pulsing dot on the bell,
// tags those posts "New", then saves the new seenAt so it won't pop open again.
// Important posts are amber and listed first. Re-checks every 2 minutes and
// when the tab regains focus. Escape or a click outside closes it.
// `onPost` (admins, who can open /announcements): a "Post an announcement"
// button at the bottom of the panel.

import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, ChevronDown, Plus } from "lucide-react";
import { forHome, isNew, posterOf, toMs } from "../../services/announcements";
import { loadAnnouncementsFor, loadSeenAt, markSeen } from "../../services/announcementService";

export const RECHECK_MS = 2 * 60 * 1000;

const fmtWhen = (ms) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export default function AnnouncementsPill({ tenantId, siteId, uid, onPost }) {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [newIds, setNewIds] = useState(() => new Set());
  const [error, setError] = useState("");
  const wrap = useRef(null);
  const busy = useRef(false);

  const check = useCallback(async () => {
    if (!uid || !tenantId || busy.current) return;
    busy.current = true;
    try {
      const [all, seenMs] = await Promise.all([loadAnnouncementsFor({ tenantId, siteId }), loadSeenAt(uid)]);
      const showing = forHome(all, Date.now());
      setPosts(showing);
      setError("");
      const fresh = showing.filter((p) => isNew(p, seenMs));
      if (fresh.length) {
        setNewIds((prev) => new Set([...prev, ...fresh.map((p) => p.id)]));
        setOpen(true);
        // Remember they've been shown, so these won't pop it open again.
        markSeen(uid).catch((e) => console.error("Couldn't save announcements seen:", e));
      }
    } catch (e) {
      console.error("Couldn't load announcements:", e);
      setError("Couldn't load announcements right now.");
    } finally {
      busy.current = false;
    }
  }, [uid, tenantId, siteId]);

  useEffect(() => {
    check();
    const t = setInterval(check, RECHECK_MS);
    const onFocus = () => check();
    const onVisible = () => document.visibilityState === "visible" && check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  // Once closed, what was new has been seen: clear the badge and tags.
  useEffect(() => {
    if (!open) setNewIds(new Set());
  }, [open]);

  // Escape or a click outside closes it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onDown = (e) => wrap.current && !wrap.current.contains(e.target) && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const newCount = posts.filter((p) => newIds.has(p.id)).length;

  return (
    <div ref={wrap} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="rx-ann-panel"
        className="rx-ann-pill"
      >
        <span className="relative inline-flex">
          <Bell className="w-4 h-4" aria-hidden="true" />
          {newCount > 0 && <span className="rx-ann-dot" aria-hidden="true" />}
        </span>
        <span>Announcements</span>
        {newCount > 0 && <span className="rx-ann-newcount">{newCount} new</span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <section id="rx-ann-panel" aria-label="Announcements" className="rx-ann-panel">
          {error ? (
            <p className="rx-home-muted text-sm">{error}</p>
          ) : posts.length === 0 ? (
            <p className="rx-home-muted text-sm">No announcements right now.</p>
          ) : (
            <ul className="space-y-2">
              {posts.map((p) => (
                <li key={p.id} className={`rx-ann-item ${p.importance === "important" ? "rx-ann-item--important" : ""}`}>
                  <div className="flex items-start gap-2">
                    <h3 className="text-sm font-semibold flex-1 min-w-0 break-words">{p.title}</h3>
                    {p.importance === "important" && <span className="rx-ann-tag rx-ann-tag--important">Important</span>}
                    {newIds.has(p.id) && <span className="rx-ann-tag rx-ann-tag--new">New</span>}
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-line break-words">{p.message}</p>
                  <p className="rx-home-muted text-[11px] mt-1.5 break-all">
                    {posterOf(p)}
                    {toMs(p.postedAt) ? ` · ${fmtWhen(Math.max(toMs(p.postedAt), toMs(p.showFrom) || 0))}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {onPost && (
            <div className="mt-3 pt-3 rx-ann-post">
              <button type="button" className="rx-home-button inline-flex items-center gap-1.5" onClick={onPost}>
                <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                Post an announcement
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
